import { useEnterAnimation } from "@/pages/services/hooks/useEnterAnimation";

/**
 * ServicesHero — top of the /services page.
 *
 * Delivers the "quiet arrival" moment:
 *   · tiny eyebrow "OUR SERVICES / INDEX"
 *   · centered serif headline "Three ways to begin."
 *   · italic subtitle "Every path starts with a conversation."
 *   · scroll hint
 *
 * Everything enters bottom-up (translateY 30 → 0, opacity 0 → 1) matching the
 * home page rhythm. No React state is used for motion.
 */
export default function ServicesHero() {
  const eyebrowRef = useEnterAnimation<HTMLDivElement>({ delay: 0 });
  const titleRef = useEnterAnimation<HTMLHeadingElement>({ delay: 140 });
  const subtitleRef = useEnterAnimation<HTMLParagraphElement>({ delay: 320 });
  const hintRef = useEnterAnimation<HTMLDivElement>({ delay: 520 });

  return (
    <section
      id="services-hero"
      className="relative w-full bg-background-50 overflow-hidden"
      style={{ minHeight: "100vh" }}
    >
      {/* Section eyebrow / meta — floats above (same treatment as home) */}
      <div className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 pt-28 md:pt-32 pointer-events-none">
        <div className="max-w-[1600px] mx-auto flex items-baseline justify-between">
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
            Our Services / Index
          </span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium hidden md:block">
            Chapter 01 · Three paths
          </span>
        </div>
      </div>

      {/* Decorative circular guides — a visual echo of the home Hero */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
      >
        <div className="absolute w-[120vh] h-[120vh] rounded-full border border-foreground-950/10"></div>
        <div className="absolute w-[95vh] h-[95vh] rounded-full border border-foreground-950/[0.08]"></div>
        <div className="absolute w-[70vh] h-[70vh] rounded-full border border-foreground-950/[0.06]"></div>
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center px-6 md:px-10 pt-40 pb-24 min-h-screen">
        <div className="max-w-[1100px] w-full flex flex-col items-center text-center">
          <div ref={eyebrowRef} className="mb-10">
            <span className="inline-flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-foreground-950/70 font-medium">
              <span className="w-8 h-[1px] bg-foreground-950/35"></span>
              A quiet beginning
              <span className="w-8 h-[1px] bg-foreground-950/35"></span>
            </span>
          </div>

          <h1
            ref={titleRef}
            className="mb-8 leading-[0.98]"
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "clamp(3.25rem, 7.5vw, 8rem)",
              fontWeight: 300,
              letterSpacing: "-0.03em",
              color: "oklch(var(--foreground-950))",
              fontVariationSettings: "'opsz' 144",
            }}
          >
            Three ways to begin.
          </h1>

          <p
            ref={subtitleRef}
            className="italic max-w-[640px]"
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "clamp(1.1rem, 1.5vw, 1.5rem)",
              fontWeight: 300,
              lineHeight: 1.55,
              letterSpacing: "0.005em",
              color: "oklch(var(--foreground-950) / 0.72)",
              fontVariationSettings: "'opsz' 144",
            }}
          >
            Every path starts with a conversation.
          </p>
        </div>

        {/* Scroll hint — bottom center */}
        <div
          ref={hintRef}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-foreground-950/55"
        >
          <span className="text-[10px] tracking-[0.3em] uppercase font-medium">
            Scroll to continue
          </span>
          <span className="w-[1px] h-10 bg-foreground-950/35"></span>
        </div>
      </div>
    </section>
  );
}